import * as React from 'react';
import type { Nullable, EntityDetail } from '@web-config-app/core';
import { JsonEditor, JsonEditorValue } from '@web-config-app/core-react-ui';
import {
  Modal,
  Button,
  StackLayout,
  HeadingText,
  InputLabel,
  StatusBanner,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { isEqual } from 'lodash-es';
import { useIntl } from '@leagueplatform/locales';

export const EntityJsonModalContainer: React.FC<{
  entityData?: Partial<EntityDetail>;
  onSetJsonClick: (entityData: Partial<EntityDetail>) => void;
}> = ({ entityData, onSetJsonClick }) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [jsonContent, setJsonContent] = React.useState<{
    json?: any;
    text: string;
  }>(() => {
    if (entityData) {
      return {
        text: JSON.stringify(entityData, null, 2),
      };
    }
    return {
      text: '{}',
    };
  });
  const [entityDataFromJson, setEntityDataFromJson] =
    React.useState<Nullable<Partial<EntityDetail>>>(null);
  const [isValidJson, setIsValidJson] = React.useState<Nullable<boolean>>(null);

  const setInitialJson = (data: Partial<EntityDetail>) => {
    setIsValidJson(true);
    setJsonContent({ text: JSON.stringify(data, null, 2) });
    setEntityDataFromJson(data);
  };
  React.useEffect(() => {
    if (entityData && !isEqual(entityData, entityDataFromJson)) {
      setInitialJson(entityData);
    }
    // disable exhaustive-deps to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityData]);

  React.useEffect(() => {
    // Reset the JSON content when the modal is closed.
    if (isOpen === false) {
      setInitialJson(entityData ?? {});
    }
    // disable exhaustive-deps to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  React.useEffect(() => {
    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'j' && evt.metaKey) {
        evt.preventDefault();
        evt.stopPropagation();
        setIsOpen(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const handleJsonChange = (content: JsonEditorValue) => {
    setJsonContent({ text: content.text });

    try {
      const entityDataFromInput = JSON.parse(content.text);
      setIsValidJson(true);
      setEntityDataFromJson(entityDataFromInput);
    } catch (error) {
      setIsValidJson(false);
      setEntityDataFromJson(null);
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <Modal.Content layout="fullscreen">
        <Modal.Title>
          <HeadingText level="1" size="xl" css={{ marginBlockEnd: '$one' }}>
            {formatMessage({ id: 'EDIT_ENTITY_JSON' })}
          </HeadingText>
        </Modal.Title>
        <StackLayout spacing="$half" horizontalAlignment="stretch">
          <StatusBanner status="warning">
            <ParagraphText>
              {formatMessage({ id: 'EDIT_JSON_WARNING_TEXT' })}
            </ParagraphText>
          </StatusBanner>
          <StackLayout orientation="horizontal" horizontalAlignment="end">
            <Button
              priority="primary"
              onClick={() => {
                if (entityDataFromJson) {
                  onSetJsonClick(entityDataFromJson);
                }

                setIsOpen(false);
              }}
              size="medium"
              disabled={!isValidJson}
            >
              {formatMessage({ id: 'SET_ENTITY_JSON' })}
            </Button>
          </StackLayout>

          <StackLayout orientation="horizontal" horizontalAlignment="stretch">
            <InputLabel css={{ width: '100%' }} htmlFor="json-input" required>
              {formatMessage({ id: 'ENTITY_JSON_INPUT_LABEL' })}
              <JsonEditor
                editorId="json-input"
                aria-describedby="json-input-status"
                editorValue={jsonContent}
                onChange={handleJsonChange}
              />
            </InputLabel>
          </StackLayout>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
